// iter v28 r190 — User M1110: palette completa sostituita con i 14 colori
// "pastel ovali" (Verde Salvia → Color Notte). Ordine cromatico verticale
// preservato (verdi → caldi → rosa → blu/viola → neutri → notte). Solo
// `notte` è isDark e applica `data-theme="dark"` sul wrapper della vetrina.

export const LAYOUTS = {
  elegante: {
    name: "Elegante",
    headingFont: "'Bodoni Moda', 'DM Serif Display', Georgia, serif",
    accentFont: "'Bodoni Moda', Georgia, serif",
    bodyFont: "'Inter', system-ui, sans-serif",
    titleStyle: { fontStyle: "italic", letterSpacing: "0", fontWeight: 400 },
    align: "left",
    pro: false,
    description: "Bodoni Moda Italic. Didone display alto-contrasto, magazine cover.",
  },
  luxury: {
    name: "Lussuoso",
    headingFont: "'Spectral', 'EB Garamond', Georgia, serif",
    accentFont: "'Spectral', 'EB Garamond', Georgia, serif",
    bodyFont: "'Inter', system-ui, sans-serif",
    titleStyle: { fontStyle: "italic", letterSpacing: "0", fontWeight: 200 },
    align: "center",
    pro: true,
    description: "Spectral ExtraLight Italic. Serif moderno fine, raffinato.",
  },
  moderno: {
    name: "Moderno",
    headingFont: "'Archivo', system-ui, sans-serif",
    accentFont: "'Archivo', system-ui, sans-serif",
    bodyFont: "'Inter', system-ui, sans-serif",
    titleStyle: { fontStyle: "normal", letterSpacing: "-0.02em", fontWeight: 900, textTransform: "uppercase" },
    align: "left",
    pro: true,
    description: "Bold sans, statement, magazine cover. Tipografia che parla.",
  },
  minimal: {
    name: "Minimal",
    headingFont: "'Space Grotesk', system-ui, sans-serif",
    accentFont: "'Space Grotesk', system-ui, sans-serif",
    bodyFont: "'Space Grotesk', system-ui, sans-serif",
    titleStyle: { fontStyle: "normal", letterSpacing: "-0.02em", fontWeight: 300 },
    align: "left",
    pro: true,
    description: "Whitespace, geometrico, Vignelli. Stile manifesto svizzero.",
  },
};

// Helper darken: per derivare un accent leggibile da un pastello chiaro.
const _darken = (hex, ratio) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const f = (v) => Math.max(0, Math.min(255, Math.round(v * (1 - ratio))));
  return `#${f(r).toString(16).padStart(2, "0")}${f(g).toString(16).padStart(2, "0")}${f(b).toString(16).padStart(2, "0")}`;
};

/**
 * iter v28 r215 — Helper di leggibilità testo su sfondo colorato.
 * Calcola la luminanza percepita (W3C relative luminance) di un HEX e
 * ritorna il colore testo più leggibile:
 *   - HEX chiaro (bianco, pastelli) → ritorna nero #1C1C1A
 *   - HEX scuro (nero, navy)        → ritorna bianco #FAFAF7
 *
 * Usato per garantire l'inversione automatica testi+logo quando una banda
 * brand è disegnata col `brand_color` salvato (es. palette Bianco → banda
 * bianca + testi neri, palette Nero → banda nera + testi bianchi).
 */
export function readableTextOn(hex) {
  if (!hex || typeof hex !== "string") return "#FAFAF7";
  const h = hex.replace("#", "");
  if (h.length !== 6) return "#FAFAF7";
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  // Relative luminance (W3C WCAG)
  const lin = (c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  const L = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  return L > 0.55 ? "#1C1C1A" : "#FAFAF7";
}

export function isLightHex(hex) {
  return readableTextOn(hex) === "#1C1C1A";
}

// Ogni pastello: bg = #FAFAF7 (carta), fg = #1C1C1A, accent = darken(swatch, 0.30)
// così abbiamo contrasto leggibile per titoli/icone su tema light.
const _light = (name, swatch) => ({
  name,
  swatch,
  bg: "#FAFAF7",
  fg: "#1C1C1A",
  softFg: "rgba(28,28,26,0.6)",
  accent: _darken(swatch, 0.30),
  border: "rgba(28,28,26,0.16)",
  isDark: false,
});

export const COLORS = {
  // ── VERDI ────────────────────────────────────────────────────
  // iter v29 r068 — User M1421: «devono essere come colore brand esattamente».
  // Il brand color Pronto è #7C8B6E (sage scuro: usato sulla landing, CTA,
  // logo). Prima `verde_salvia` era #A8B59A (più chiaro/pastello) e nelle
  // brochure/volantini il colore della banda non corrispondeva al brand
  // configurato in /profilo. Adesso il PRIMO colore della palette social
  // (verde_salvia) è esattamente il brand color Pronto.
  verde_salvia:       _light("Verde Pronto",        "#7C8B6E"),
  verde_tenue:        _light("Verde Salvia",        "#A8B59A"),
  // ── GIALLI / BEIGE ──────────────────────────────────────────
  giallo_pastello:    _light("Giallo Pastello",    "#F2D78A"),
  beige_sabbia:       _light("Beige Sabbia",       "#E2C89E"),
  // ── TERRACOTTA / ROSSI ──────────────────────────────────────
  terracotta_chiaro:  _light("Terracotta Chiaro",  "#F0AE8D"),
  terracotta_classico:_light("Terracotta Classico","#D47A54"),
  rosso_polvere:      _light("Rosso Polvere",      "#C86A5D"),
  // ── ROSA / VIOLA ────────────────────────────────────────────
  rosa_cipria:        _light("Rosa Cipria",        "#E2BFE6"),
  lavanda_polvere:    _light("Lavanda Polvere",    "#B7CCE6"),
  // ── BLU ─────────────────────────────────────────────────────
  blu_polvere:        _light("Blu Polvere",        "#8DA0B9"),
  // ── NEUTRI ──────────────────────────────────────────────────
  grigio_perla:       _light("Grigio Perla",       "#D1D5D9"),
  marrone_scuro:      _light("Marrone Scuro",      "#A68A7D"),
  // iter v28 r215 — User M1136: per Bianco e Nero, il brand_color salvato
  // (accent) DEVE coincidere col colore visuale della palette così la "banda
  // bianca = banda bianca" e "banda nera = banda nera". Il colore del testo
  // sopra la banda viene calcolato dinamicamente con isLightHex() (sotto).
  bianco:             { name: "Bianco", swatch: "#FFFFFF", bg: "#FAFAF7", fg: "#1C1C1A", softFg: "rgba(28,28,26,0.6)", accent: "#FFFFFF", border: "rgba(28,28,26,0.18)", isDark: false },
  nero:               { name: "Nero",   swatch: "#0F0F0F", bg: "#0F0F0F", fg: "#FAFAF7", softFg: "rgba(250,250,247,0.65)", accent: "#0F0F0F", border: "rgba(250,250,247,0.22)", isDark: true },
  // ── DARK (modalità notte vera, con accent oro) ─────────────
  notte: {
    name: "Notte",
    swatch: "#2B3654",
    bg: "#2B3654",
    fg: "#FAFAF7",
    softFg: "rgba(250,250,247,0.65)",
    accent: "#C8B886", // oro caldo
    border: "rgba(200,184,134,0.30)",
    isDark: true,
  },
};

// iter v28 r248 — User M1167: ripetizione colori nei modal Brochure/Volantino.
// Causa: Object.entries(COLORS) include i 10 alias retro-compat sotto, che
// puntano agli stessi oggetti canonici. Esportiamo l'elenco canonico (15
// chiavi) da usare nei picker invece di `Object.entries(COLORS)`.
export const CANONICAL_COLOR_KEYS = Object.freeze([
  "verde_salvia",
  "verde_tenue",
  "giallo_pastello",
  "beige_sabbia",
  "terracotta_chiaro",
  "terracotta_classico",
  "rosso_polvere",
  "rosa_cipria",
  "lavanda_polvere",
  "blu_polvere",
  "grigio_perla",
  "marrone_scuro",
  "bianco",
  "nero",
  "notte",
]);

export const CANONICAL_COLOR_ENTRIES = Object.freeze(
  CANONICAL_COLOR_KEYS.map((k) => [k, COLORS[k]])
);

// iter v28 r191 — back-compat alias. La palette è stata rinominata da
// `verde_brera` → `verde_salvia` ma molti template (Brochure, Volantino,
// SocialPosts, Stories) hanno ancora `color = "verde_brera"` come default
// hard-coded → `COLORS.verde_brera` ritornava undefined → crash su `.bg`.
// Aggiungiamo gli alias storici come puntatori al primo colore disponibile
// così tutti i fallback `|| COLORS.verde_brera` continuano a funzionare.
COLORS.verde_brera = COLORS.verde_salvia;
COLORS.bosco = COLORS.verde_tenue;
COLORS.senape = COLORS.giallo_pastello;
COLORS.terracotta = COLORS.terracotta_classico;
COLORS.cioccolato = COLORS.marrone_scuro;
COLORS.polvere_rosa = COLORS.rosa_cipria;
COLORS.bordeaux = COLORS.rosso_polvere;
COLORS.lavanda = COLORS.lavanda_polvere;
COLORS.azzurro_polvere = COLORS.blu_polvere;
COLORS.nebbia = COLORS.grigio_perla;
// NB: in passato `nero` era un alias per `notte`. Ora `nero` è una palette
// indipendente (vero nero monocromatico). Per back-compat con stato vecchio
// che potrebbe ancora avere salvato hex `#C8B886` (accent notte), il match
// avviene comunque su `accent` dentro BrandColorPicker.
